"use client";

import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SearchCheck, FileCode2, MapPin, Star, Code as Code2, ChartLine as LineChart, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const steps = [{
  number: "01",
  title: "In-Depth SEO Audit",
  icon: SearchCheck,
  shortDesc: "A complete diagnostic of your technical infrastructure, content gaps, competitor landscapes, and search visibility bottlenecks.",
  deliverables: ["Technical crawl error analysis", "Keyword opportunity mapping", "Backlink health & toxicity check"]
}, {
  number: "02",
  title: "Strategic Optimization",
  icon: FileCode2,
  shortDesc: "Executing targeted on-page technical fixes, metadata refinements, content restructuring, and internal linking strategies.",
  deliverables: ["Title & meta description overhaul", "Intent-focused content structuring", "URL architecture optimization"]
}, {
  number: "03",
  title: "Citations & Authority",
  icon: MapPin,
  shortDesc: "Building consistent, high-trust local and national citation signals across premier directory platforms.",
  deliverables: ["NAP consistency audit & sync", "Tier-1 industry directory submissions", "Entity & trust signal mapping"]
}, {
  number: "04",
  title: "Reviews & Reputation",
  icon: Star,
  shortDesc: "Implementing authentic review acquisition workflows to elevate customer trust and strengthen local search rankings.",
  deliverables: ["Google Business Profile optimization", "Review generation strategies", "Sentiment monitoring guidance"]
}, {
  number: "05",
  title: "Schema & AI Search Markup",
  icon: Code2,
  shortDesc: "Deploying advanced structured data so Google and modern AI engines precisely index your business entities and services.",
  deliverables: ["LocalBusiness & Organization schema", "Service & Product rich snippets", "FAQ & Article structured data"]
}, {
  number: "06",
  title: "Transparent ROI Reporting",
  icon: LineChart,
  shortDesc: "Clear, actionable monthly reporting focused on organic rankings, conversions, keyword momentum, and direct strategic advice.",
  deliverables: ["Live performance dashboard", "Direct video commentary from Yassine", "Actionable monthly roadmaps"]
}];
export default function Methodology() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".methodology-header", {
        y: -20
      });
      gsap.to(".methodology-header", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
      gsap.set(".methodology-step", {
        y: 30
      });
      gsap.to(".methodology-step", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.15,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 75%",
          once: true
        }
      });
      gsap.set(".methodology-cta", {
        y: 20
      });
      gsap.to(".methodology-cta", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".methodology-cta",
          start: "top 85%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".methodology-header, .methodology-step, .methodology-cta", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  return <section id="methodologyprocessflow" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 overflow-hidden">
      <div ref={containerRef} className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        {}
        <div className="methodology-header text-center max-w-3xl mx-auto mb-16 md:mb-20" style={{
        opacity: 0
      }}>
          <Badge variant="secondary" className="mb-4 text-xs tracking-wide uppercase px-3 py-1 font-medium bg-secondary text-secondary-foreground border border-accent/30">
            Proven Execution Framework
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-serif">
            My Step-by-Step SEO Methodology
          </h2>
          <p className="mt-4 text-base md:text-lg text-muted-foreground font-sans leading-relaxed">
            When you work directly with me, every campaign follows a structured, transparent process engineered to build long-term search dominance and measurable commercial impact.
          </p>
        </div>

        {}
        <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {steps.map((step, i) => {
          const IconComponent = step.icon;
          return <div key={step.number} data-index={i} className="methodology-step relative flex flex-col" style={{
            opacity: 0
          }}>
                {}
                {i < steps.length - 1 && <div aria-hidden="true" className="hidden lg:block absolute top-12 left-full w-8 h-[2px] bg-accent/40 z-0 transform -translate-x-4" />}

                <Card className="flex-1 bg-card border border-border shadow-xs hover:shadow-md hover:border-primary/40 transition-all duration-300 relative z-10 flex flex-col justify-between group rounded-xl">
                  <CardContent className="p-6 md:p-8 flex flex-col h-full">
                    {}
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary text-primary-foreground font-semibold font-serif text-lg shadow-xs group-hover:bg-primary/90 transition-colors">
                        {step.number}
                      </div>
                      <div className="p-2.5 rounded-lg bg-secondary text-accent group-hover:bg-accent group-hover:text-accent-foreground transition-colors">
                        <IconComponent className="w-6 h-6" />
                      </div>
                    </div>

                    {}
                    <h3 className="text-xl font-semibold text-foreground mb-3 font-serif group-hover:text-primary transition-colors">
                      {step.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-6 font-sans">
                      {step.shortDesc}
                    </p>

                    {}
                    <div className="mt-auto pt-4 border-t border-border/60">
                      <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-3">
                        Key Deliverables
                      </p>
                      <ul className="space-y-2">
                        {step.deliverables.map((item, dIdx) => <li key={dIdx} data-index={dIdx} className="text-xs text-muted-foreground flex items-start gap-2 font-sans">
                            <CheckCircle2 className="w-3.5 h-3.5 text-accent shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>

        {}
        <div className="methodology-cta mt-16 md:mt-20 p-8 md:p-12 rounded-2xl bg-muted/50 border border-border text-center max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 shadow-xs" style={{
        opacity: 0
      }}>
          <div className="text-left max-w-xl">
            <h3 className="text-2xl md:text-3xl font-bold text-foreground font-serif">
              Ready for sustainable search growth?
            </h3>
            <p className="text-muted-foreground text-sm md:text-base mt-2 font-sans">
              Book a direct consultation with me. We will discuss your website goals, analyze current barriers, and chart an actionable SEO strategy.
            </p>
          </div>
          <div className="shrink-0">
            <Button asChild size="lg" className="font-medium gap-2">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}